/**
 * IANA time-zone helpers for Remote Console set_timezone (T25).
 *
 * Prefer the runtime ICU/IANA set via Intl.supportedValuesOf("timeZone"),
 * which aligns with the ZoneIds modern Android exposes. Never invent zones.
 */

/** Cached sorted IANA identifiers for the current JS runtime. */
let cachedZones: readonly string[] | null = null;

/**
 * Full sorted list of IANA time-zone identifiers supported by this runtime.
 * Falls back to validating known candidates if supportedValuesOf is absent.
 */
export function listSupportedIanaTimeZones(): readonly string[] {
  if (cachedZones) return cachedZones;
  const intlWithSupported = Intl as typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };
  if (typeof intlWithSupported.supportedValuesOf === "function") {
    const zones = intlWithSupported
      .supportedValuesOf("timeZone")
      .filter((z) => typeof z === "string" && z.length > 0)
      .slice()
      .sort((a, b) => a.localeCompare(b));
    cachedZones = Object.freeze(zones);
    return cachedZones;
  }
  // Legacy engines: empty list — UI shows message; validation still uses isValidIanaTimeZone.
  cachedZones = Object.freeze([]);
  return cachedZones;
}

/** True when `tz` is a valid IANA identifier for this runtime (rejects EST/CST). */
export function isValidIanaTimeZone(tz: string): boolean {
  if (typeof tz !== "string") return false;
  const trimmed = tz.trim();
  if (!trimmed || trimmed.includes(" ") || !trimmed.includes("/")) {
    // Allow "UTC" / "GMT" which some Android images report
    if (trimmed !== "UTC" && trimmed !== "GMT") return false;
  }
  try {
    Intl.DateTimeFormat("en-US", { timeZone: trimmed }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

/** Filter zones by substring (case-insensitive). Empty query → full list. */
export function filterIanaTimeZones(
  query: string,
  zones: readonly string[] = listSupportedIanaTimeZones()
): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return zones.slice();
  return zones.filter((z) => z.toLowerCase().includes(q));
}

/** Reset cache (tests only). */
export function resetIanaTimeZoneCacheForTests(): void {
  cachedZones = null;
}
